<template>
  <div class="max-container">
    <!-- Loading skeleton -->
    <template v-if="state.loading">
      <div class="flex gap-6">
        <div class="hidden md:block">
          <div class="h-96 w-72 bg-gray-200 animate-pulse rounded" />
        </div>
        <div class="flex flex-col w-full gap-6">
          <div
            v-for="i in 3"
            :key="i"
            class="h-28 w-full bg-gray-200 animate-pulse rounded"
          />
        </div>
      </div>
    </template>

    <!-- No results state -->
    <template v-else-if="state.results.length < 1 && state.hasRunSearch">
      <div class="text-center py-8">
        <p class="text-gray-700 text-base mb-4">
          Your search retrieved no results. Search again.
        </p>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 border border-transparent rounded-xs transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          @click="startOver"
        >
          Try Again
        </button>
      </div>
    </template>

    <!-- Error state -->
    <template v-else-if="state.error">
      <div class="text-gray-700 text-base">
        Oops! Something went wrong. Please modify your search and try again.
      </div>
    </template>

    <!-- Main content: Filters and results -->
    <div
      v-else
      class="flex flex-col md:flex-row gap-6 w-full"
    >
      <!-- Left sidebar: Search filters -->
      <div
        v-if="state.info?.facets"
        class="relative border border-gray-200 rounded-xs md:border-0 md:p-0 md:rounded-none shrink-0 md:w-72"
      >
        <div id="filters">
          <!-- Mobile-only filter toggle button -->
          <button
            class="md:hidden w-full p-4 text-red-600 hover:bg-gray-25 rounded-xs text-left font-semibold"
            type="button"
            @click="showMobileFilters = !showMobileFilters"
          >
            Filter
          </button>

          <!-- Filter groups container -->
          <span class="sr-only">Filter</span>
          <div
            v-if="state.info?.facets"
            :class="{
              'hidden': !showMobileFilters,
            }"
            class="md:block p-4 md:p-0 space-y-4"
          >
            <!-- Clear all filters action button -->
            <div v-if="hasActiveFilters" class="mb-4">
              <button
                type="button"
                class="w-full px-3 py-2 text-sm text-gray-600 hover:text-red-700 border border-gray-200 hover:border-gray-300 rounded-xs hover:bg-red-50 transition-colors"
                @click="clearAllFilters"
              >Clear all filters</button>
            </div>

            <!-- Keyword search input with debounced search -->
            <div class="mb-4 p-3 rounded-xs bg-gray-50">
              <label for="filter-keyword">
                <div class="text-lg mb-4 font-bold">Keyword Filter</div>
              </label>
              <input
                id="filter-keyword"
                v-model="state.keywordFilter"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-xs focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Filter by keyword"
                @input="onKeywordInput"
                @keyup.enter="onKeywordSearch"
              >
            </div>

            <!-- Dynamic facet filter groups (topic, delivery method, etc.) -->
            <training-list-facet-group
              v-for="key in nonEmptyFacetKeys"
              :key="key"
              :facet-name="key"
              :facet-obj="state.info?.facets[key]"
              :selected="state.selectedFacets[key] || []"
              :expanded="state.expandedFacets[key] ?? false"
              :delivery="delivery"
              @update:selected="updateSelectedFacets(key, $event)"
              @update:expanded="updateExpandedFacets(key)"
            />

            <!-- Date range filter -->
            <div class="mb-4 p-3 rounded-xs bg-gray-50" v-if="delivery && delivery !== 'online'">
              <div class="text-lg mb-4 font-bold">Date Range</div>

              <!-- Start date input -->
              <div class="mb-3">
                <label
                  for="filter-start-date"
                  class="block text-sm font-medium text-gray-700 mb-1"
                >
                  Start Date
                </label>
                <input
                  id="filter-start-date"
                  v-model="state.startDate"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-xs focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  @change="onDateChange"
                >
              </div>

              <!-- End date input -->
              <div>
                <label
                  for="filter-end-date"
                  class="block text-sm font-medium text-gray-700 mb-1"
                >
                  End Date
                </label>
                <input
                  id="filter-end-date"
                  v-model="state.endDate"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-xs focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  :min="state.startDate"
                  @change="onDateChange"
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right content: Search results and pagination -->
      <div class="grow">
        <div>
          <!-- Results summary and pagination controls -->
          <div
            v-if="state.total_result_count > 0"
            class="flex flex-col sm:flex-row w-full gap-4 items-center mb-4"
          >
            <div class="grow">
              <p class="grow text-gray-500 text-base">
                {{ showingResultsText }}
              </p>
            </div>
            <!-- Results per page dropdown -->
            <div class="flex items-center gap-2">
              <label
                class="flex items-center gap-2 text-gray-700 text-sm whitespace-nowrap"
              >
                <span>Results per page</span>
                <select
                  v-model="state.perPage"
                  class="rounded-none border border-gray-100 hover:border-gray-200"
                  aria-label="Results per page"
                  @change="setResultsPerPage"
                >
                  <option
                    v-for="item in [10, 25, 50]"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </option>
                </select>
              </label>
            </div>
          </div>

          <!-- Individual search result items -->
          <training-result
            v-for="result in state.results"
            :key="JSON.stringify(result)"
            :result="result"
            :query="params.get('query') || ''"
          />

          <!-- Page navigation component -->
          <search-paginator
            v-if="state.info?.num_pages > 1"
            :current-page="state.page"
            :num-pages="state.info?.num_pages || 1"
            @go-to-page="gotoPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted } from 'vue';
import { ofetch } from 'ofetch'

// =============================================================================
// WAGTAIL BOILERPLATE & PROPS
// =============================================================================

/**
 * Interface for the search page component props
 * @property uuid - Unique identifier for the component instance
 * @property endpoint - API endpoint for fetching training courses
 * @property delivery - Delivery method filter for courses
 */
interface SearchPage {
  uuid: string,
  vueId: string,
  endpoint: string,
  delivery: string,
}

/**
 * Component props with default values
 * The uuid is used to identify this specific instance of the component on the page
 */
const props = withDefaults(defineProps<SearchPage>(), {
  uuid: '',
  vueId: 'training-list',
  endpoint: '',
  delivery: '',
});

// =============================================================================
// TYPESCRIPT INTERFACES
// =============================================================================

/**
 * Interface for search API response metadata
 * Contains pagination info, facet data, and result counts
 */
interface ISearchInfo {
  current_page?: number
  facets?: {
    sei_tags?: Record<string, number>
    delivery_methods?: Record<string, number>
    exams?: Record<string, number>
  }
  num_pages?: number
  per_page?: number
  total_result_count?: number
  keywordFilter?: string
}

/**
 * Interface for individual search result items
 * Represents a single training course returned from the API
 */
export interface ISearchResult {
  title?: string
  html_url?: string
  short_description?: string
  categories?: Record<string, unknown>
  delivery_methods?: Record<string, unknown>
  meta?: {
    slug?: string
  }
  [key: string]: unknown
}

/**
 * Reference to the root DOM element for this component instance
 * Used to clean up data attributes after initialization
 */
const rootElement = ref<HTMLElement | null>(null)

/**
 * Reactive endpoint value read from DOM or props
 */
const endpointUrl = ref('')
const delivery = ref('')

onMounted(() => {
  // Try multiple approaches to find the element
  let element = null

  // First try: use uuid if it exists
  if (props.uuid) {
    element = document.querySelector(`[data-vue-id="${props.vueId}"][data-uuid="${props.uuid}"]`)
  }

  // Second try: use data-vue-id (more reliable)
  if (!element) {
    element = document.querySelector(`[data-vue-id="${props.vueId}"]`)
  }

  rootElement.value = element as HTMLElement

  if (rootElement.value?.dataset) {
    // Read endpoint from data attribute
    endpointUrl.value = rootElement.value.dataset.endpoint || props.endpoint || '/api/v2/training/'

    // Add this line to read the delivery data attribute
    delivery.value = rootElement.value.dataset.delivery || props.delivery || 'all'
    // eslint-disable-next-line vue/no-setup-props-destructure
    console.log('delivery value:', delivery.value)

    // Clean up
    delete rootElement.value.dataset['json']
  } else {
    endpointUrl.value = props.endpoint || '/api/v2/training/'
    delivery.value = props.delivery || 'all'
  }

  // Now call the API after endpoint is set
  callApi()
})

// =============================================================================
// URL QUERY PARAMETER HELPERS
// =============================================================================

/**
 * Recursively decodes URI components to handle multiple encoding layers
 * Prevents issues with special characters in search parameters
 * @param str - The string to decode
 * @returns Fully decoded string
 */
function fullyDecodeURIComponent(str: string): string {
  let prev;
  let decoded = str;
  do {
    prev = decoded;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      break;
    }
  } while (prev !== decoded);
  return decoded;
}

/**
 * Gets current URL search parameters
 * Returns empty URLSearchParams if running server-side
 */
const getQueryParams = () => typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)

/**
 * Parses facet filters from URL parameters
 * Looks for parameters starting with 'facet_' and groups them by facet name
 * @param params - URLSearchParams object to parse
 * @returns Object mapping facet names to arrays of selected values
 */
const parseFacetsFromParams = (params: URLSearchParams): Record<string, string[]> => {
  const facets: Record<string, string[]> = {}
  params.forEach((value, key) => {
    if (key.startsWith('facet_')) {
      const facetName = key.slice(6) // Remove 'facet_' prefix
      if (!facets[facetName]) facets[facetName] = []
      facets[facetName].push(fullyDecodeURIComponent(value))
    }
  })
  return facets
}

/**
 * Updates the browser URL to reflect current search state
 * Maintains URL sync without triggering page reload
 * @param options - Object containing page, perPage, selectedFacets, keywordFilter, startDate, and endDate
 */
const updateUrlQuery = ({ page, perPage, selectedFacets, keywordFilter, startDate, endDate }: { page: number, perPage: number, selectedFacets: Record<string, string[]>, keywordFilter: string, startDate: string, endDate: string }) => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams()

  // Only add non-default values to keep URL clean
  if (page > 1) params.set('page', String(page))
  if (perPage !== 10) params.set('per_page', String(perPage))

  // Add facet filters as separate parameters
  for (const [facet, values] of Object.entries(selectedFacets)) {
    for (const value of values) {
      params.append(`facet_${facet}`, fullyDecodeURIComponent(value))
    }
  }

  // Add keyword filter if present
  if (keywordFilter && keywordFilter.trim() !== '') {
    params.set('keywordFilter', fullyDecodeURIComponent(keywordFilter.trim()))
  }

  // Add date filters if present
  if (startDate) {
    params.set('start_date', startDate)
  }
  if (endDate) {
    params.set('end_date', endDate)
  }

  const url = new URL(window.location.href)
  url.search = params.toString()
  window.history.replaceState({}, '', url.toString())
}

// =============================================================================
// STATE INITIALIZATION
// =============================================================================

/**
 * Initialize search state from current URL parameters
 * Allows bookmarking and sharing of search results
 */
const params = getQueryParams()
const initialPage = parseInt(params.get('page') || '1', 10)
const initialPerPage = parseInt(params.get('per_page') || '10', 10)
const initialFacets = parseFacetsFromParams(params)
const initialStartDate = typeof window !== 'undefined' ? params.get('start_date') || '' : ''
const initialEndDate = typeof window !== 'undefined' ? params.get('end_date') || '' : ''

// =============================================================================
// REACTIVE STATE
// =============================================================================

/**
 * Controls mobile filter panel visibility
 * Only affects mobile/tablet layouts where filters are collapsible
 */
const showMobileFilters = ref(false)

/**
 * Main application state object
 * Contains all search-related data and UI state
 */
const state = reactive({
  info: null as ISearchInfo | null,
  results: [] as ISearchResult[],
  loading: false,
  hasRunSearch: false,
  error: null as string | null,
  perPage: initialPerPage,
  page: initialPage,
  selectedFacets: initialFacets as Record<string, string[]>,
  expandedFacets: {} as Record<string, boolean>,
  total_result_count: 0,
  keywordFilter: '',
  startDate: initialStartDate,
  endDate: initialEndDate,
})

// =============================================================================
// DEBOUNCED SEARCH FUNCTIONALITY
// =============================================================================

/**
 * Timeout reference for debounced keyword search
 * Prevents excessive API calls while user is typing
 */
let keywordSearchTimeout: ReturnType<typeof setTimeout> | null = null

/**
 * Handles keyword input changes with debouncing
 * Triggers search after 1.5 seconds of inactivity
 */
const onKeywordInput = () => {
  // Clear existing timeout to reset the delay
  if (keywordSearchTimeout) {
    clearTimeout(keywordSearchTimeout)
  }

  // Set new timeout for debounced search (1500ms delay)
  keywordSearchTimeout = setTimeout(() => {
    onKeywordSearch()
  }, 1500)
}

/**
 * Executes immediate keyword search
 * Called when user presses Enter or after debounce delay
 */
const onKeywordSearch = () => {
  // Clear timeout if user presses enter (immediate search)
  if (keywordSearchTimeout) {
    clearTimeout(keywordSearchTimeout)
    keywordSearchTimeout = null
  }

  // Reset to first page when performing new search
  state.page = 1
  state.keywordFilter = state.keywordFilter.trim()
  callApi()
}

/**
 * Handles date filter changes
 * Triggers search immediately when date is changed
 */
const onDateChange = () => {
  // Reset to first page when date filters change
  state.page = 1
  callApi()
}

// =============================================================================
// COMPUTED PROPERTIES
// =============================================================================

/**
 * Defines the display order for facet groups
 * Lower numbers appear first in the filter sidebar
 */
const facet_order: Record<string, number> = {
  AssetTypeName: 1,
  sei_tags: 2,
  delivery_methods: 3,
  exams: 4
}

/**
 * Returns facet keys that have available filter options, sorted by preference
 * Filters out empty facets and maintains consistent ordering
 */
const nonEmptyFacetKeys = computed(() =>
  Object.keys(state.info?.facets || {})
    .filter(key => {
      const facetData = state.info?.facets?.[key] || {}
      const facetCount = Object.keys(facetData).length
      return facetCount > 0
    })
    .sort((a, b) => (facet_order[a] || 99) - (facet_order[b] || 99))
)

/**
 * Determines if any filters are currently active
 * Used to show/hide the "Clear all filters" button
 */
const hasActiveFilters = computed(() => {
  const hasFacetFilters = Object.keys(state.selectedFacets).length > 0
  const hasKeywordFilter = state.keywordFilter && state.keywordFilter.trim().length > 0
  const hasDateFilters = state.startDate || state.endDate
  return hasFacetFilters || hasKeywordFilter || hasDateFilters
})

/**
 * Generates the "Results X-Y of Z" display text
 * Calculates the range of currently displayed results
 */
const showingResultsText = computed(() => {
  if (!state.results.length) return 'Your search retrieved no results. Search again.'

  const info = state.info
  const page = info?.current_page || 1
  const perPage = info?.per_page || state.results.length || 10
  const total = state.total_result_count || 0
  const start = ((page - 1) * perPage) + 1
  const end = Math.min(page * perPage, total)

  return `Results ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()}`
})

// =============================================================================
// SEARCH AND FILTER FUNCTIONS
// =============================================================================

/**
 * Performs a fresh search, clearing existing results and filters
 * Used for new searches from external components
 */
const search = () => {
  state.results = []
  state.info = null
  state.selectedFacets = {}
  state.keywordFilter = ''
  state.page = 1
  callApi()
}

/**
 * Clears all active filters and performs new search
 * Resets both facet filters, keyword search, and date filters
 */
const clearAllFilters = () => {
  state.page = 1
  state.selectedFacets = {}
  state.keywordFilter = ''
  state.startDate = ''
  state.endDate = ''
  callApi()
}

/**
 * Resets all search state and performs a fresh search
 * Used when no results are found to help users start over
 */
const startOver = () => {
  state.results = []
  state.info = null
  state.selectedFacets = {}
  state.keywordFilter = ''
  state.startDate = ''
  state.endDate = ''
  state.page = 1
  state.hasRunSearch = false
  callApi()
}

/**
 * Toggles a facet filter value on/off
 * @param facetName - The facet category (e.g., 'sei_tags')
 * @param value - The specific value to toggle
 */
const updateSelectedFacets = (facetName: string, value: string) => {
  showMobileFilters.value = false // Close mobile filters after selection

  const newFacets = { ...state.selectedFacets }
  const arr = Array.isArray(newFacets[facetName]) ? [...newFacets[facetName]] : []
  const idx = arr.indexOf(value)

  // Toggle the value - add if not present, remove if present
  if (idx !== -1) {
    arr.splice(idx, 1)
  } else {
    arr.push(value)
  }

  // Clean up empty facet arrays
  if (arr.length === 0) {
    delete newFacets[facetName]
  } else {
    newFacets[facetName] = arr
  }

  state.selectedFacets = newFacets
  state.page = 1 // Reset to first page when filters change
  callApi()
}

/**
 * Toggles the expanded/collapsed state of a facet group
 * @param facetName - The facet category to expand/collapse
 */
const updateExpandedFacets = (facetName: string) => {
  state.expandedFacets[facetName] = !state.expandedFacets[facetName]
}

/**
 * Updates results per page setting and refreshes search
 * Resets to page 1 since pagination numbers change
 */
const setResultsPerPage = () => {
  state.page = 1
  callApi()
}

/**
 * Navigates to a specific page number
 * @param page - The target page number (1-based)
 */
const gotoPage = (page: number) => {
  if (!state.info) return

  const numPages = Math.ceil(state.total_result_count / state.perPage) || 1

  // Validate page number and check if it's different from current
  if (page < 1 || page > numPages || page === (state.info.current_page || 1)) return

  state.page = page
  callApi()
}

// =============================================================================
// API ENDPOINT CONFIGURATION
// =============================================================================

/**
 * Computed property for the API endpoint
 * Uses the endpoint from DOM data attribute, prop, or fallback
 */
const apiEndpoint = computed(() => {
  return endpointUrl.value || props.endpoint || '/api/v2/training/'
})

// =============================================================================
// API COMMUNICATION
// =============================================================================

/**
 * Main API function that fetches search results from the backend
 * Handles all search parameters including pagination, filters, keywords, and dates
 */
const callApi = async () => {
  const { page, perPage, selectedFacets, keywordFilter, startDate, endDate } = state

  // Set loading state and clear any previous errors
  state.loading = true
  state.hasRunSearch = true
  state.error = null

  try {
    // Build base API parameters for the Wagtail API
    const apiParams: Record<string, string | number> = {
      fields: '*',
      limit: perPage,
      offset: (page - 1) * perPage,
    }

    // Add keyword search parameter as title filter if provided
    if (keywordFilter && keywordFilter.trim()) {
      apiParams.title = keywordFilter.trim()
    }

    // Add date range filters if provided
    if (startDate) {
      apiParams.start_date = startDate
    }
    if (endDate) {
      apiParams.end_date = endDate
    }

    // Map frontend facet names to backend API filter parameters
    // This translation layer allows the frontend to use user-friendly names
    const filterMap: Record<string, string> = {
      'sei_tags': 'categories',
      'delivery_methods': 'delivery_methods',
      'exams': 'exams',
    }

    // Add facet filters to API parameters
    if (delivery.value && delivery.value !== 'all') {
      apiParams.delivery_methods = delivery.value
    }

    for (const [facetName, values] of Object.entries(selectedFacets)) {
      if (values.length > 0) {
        if (facetName === 'delivery_methods' && delivery.value && delivery.value !== 'all') {
          // Skip adding delivery_methods filter if it matches the global delivery filter
          continue
        }
        console.log('Processing facet:', facetName, 'with values:', values)
        const backendFilterName = filterMap[facetName]
        if (backendFilterName) {
          apiParams[backendFilterName] = values.join(',')
        }
      }
    }

    // Make the API request to the dynamic endpoint
    const data = await ofetch(apiEndpoint.value, {
      method: 'GET',
      params: apiParams,
    })

    // Process successful response
    state.info = {
      current_page: page,
      per_page: perPage,
      total_result_count: data.meta.total_count || 0,
      facets: data.meta.facets || {},
      num_pages: Math.ceil((data.meta.total_count || 0) / perPage),
      keywordFilter: keywordFilter,
    }
    state.results = data.items || []
    state.error = null
    state.total_result_count = data.meta.total_count || 0

  } catch (err) {
    // Handle API errors gracefully
    state.error = err instanceof Error ? err.message : 'An error occurred'
    state.info = null
    state.results = []
  } finally {
    state.loading = false

    // Keep browser URL synchronized with current search state
    // This enables bookmarking and sharing of search results
    updateUrlQuery({
      page: state.page,
      perPage: state.perPage,
      selectedFacets: state.selectedFacets,
      keywordFilter: state.keywordFilter,
      startDate: state.startDate,
      endDate: state.endDate,
    })
  }
}
</script>
